import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Button
} from '@mui/material';
import {
  DataUsage,
  Lightbulb as LightbulbIcon,
  TrendingDown,
  TrendingUp,
  Balance,
  Assessment as AssessmentIcon,
  Tag as TagIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks';
import { logger } from '../utils/logger';
import apiService, { DashboardSummary } from '../services/api';
import { KpiCard } from '../components/dashboard/KpiCard';
import { CtaSection } from '../components/dashboard/CtaSection';
import { RecentSimulations } from '../components/dashboard/RecentSimulations';
import { RecentIdeas } from '../components/dashboard/RecentIdeas';
import OnboardingModal from '../components/OnboardingModal';
import TrialStatusModal from '../components/TrialStatusModal';
import SubscriptionExpiredModal from '../components/SubscriptionExpiredModal';
import SubscriptionCancellationBanner from '../components/SubscriptionCancellationBanner';
import useTrialExpirationHandler from '../hooks/useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from '../hooks/useSubscriptionExpirationHandler';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { Divider } from '@mui/material';
import {
  formatLargeNumber,
  formatImpressions,
  formatEngagement,
  formatReputationPoints,
  getKPIColor
} from '../utils/kpiFormatting';

// Use the DashboardSummary interface from api.ts

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [orgInfo, setOrgInfo] = useState<any>(null);
  const [kpiData, setKpiData] = useState<any>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiError, setKpiError] = useState<string | null>(null);
  
  const { user, landingFormData, showOnboarding, setShowOnboarding } = useAuth();
  const navigate = useNavigate();

  // Trial expiration handling
  const { 
    isExpiredModalOpen: isTrialExpiredModalOpen, 
    isAdmin: isTrialAdmin, 
    handleApiError: handleTrialApiError, 
    closeExpiredModal: closeTrialExpiredModal 
  } = useTrialExpirationHandler();

  // Subscription expiration handling
  const { 
    isExpiredModalOpen: isSubscriptionExpiredModalOpen, 
    isAdmin: isSubscriptionAdmin, 
    handleApiError: handleSubscriptionApiError, 
    closeExpiredModal: closeSubscriptionExpiredModal 
  } = useSubscriptionExpirationHandler();
  
  // Trial status check for showing modal after login
  const { 
    showTrialExpiredModal, 
    showTrialExpiringModal,
    setShowTrialExpiredModal,
    setShowTrialExpiringModal,
    daysUntilExpiry 
  } = useTrialStatus();

  // Set page title
  usePageTitle('Dashboard');

  // Helper function to create rich tooltip content for impact analysis cards
  const createImpactTooltip = (
    type: 'backlash-prevented' | 'boost-achieved' | 'net-impact',
    data?: any,
    narrative?: string
  ) => {
    const getTitle = () => {
      switch (type) {
        case 'backlash-prevented': return 'Reputation Backlash Prevented';
        case 'boost-achieved': return 'Reputation Boost Achieved';
        case 'net-impact': return 'Net Impact Analysis';
      }
    };

    const getMethodology = () => {
      switch (type) {
        case 'backlash-prevented':
          return 'Calculated from unpublished posts that met negative thresholds (repDelta < 0 AND negativePct ≥ 50%). Shows the negative impact avoided by not publishing risky content.';
        case 'boost-achieved':
          return 'Calculated from published posts that met positive thresholds (repDelta > 0 OR positivePct ≥ 60%). Shows the positive impact generated from successful content.';
        case 'net-impact':
          return 'Combined view showing your overall content impact. Calculated as positive gains minus avoided negative impact across impressions, engagement, and reputation points.';
      }
    };

    const getDataContext = () => {
      if (!data) return '';
      switch (type) {
        case 'backlash-prevented':
          const postsAvoided = data.postsAvoided || 0;
          return `Based on ${postsAvoided} flagged ${postsAvoided === 1 ? 'post' : 'posts'}${data.crisisRiskAvoided ? ` with ${data.crisisRiskAvoided} total crisis risk avoided` : ''}. Uses platform-specific impression ratios (Twitter/X: likes×25 + replies×120).`;
        case 'boost-achieved':
          const postsCount = data.postsCount || 0;
          return `Based on ${postsCount} published ${postsCount === 1 ? 'post' : 'posts'}${data.reputationPointsAdded ? ` generating ${data.reputationPointsAdded} reputation points` : ''}. Engagement calculated as (likes + replies) × positive percentage.`;
        case 'net-impact':
          return 'Net calculation: positive gains minus avoided negatives. Reputation points represent the predicted change in your brand\'s reputation score. Negative values indicate more potential damage prevented than gains achieved.';
        default:
          return '';
      }
    };

    return (
      <Box sx={{ maxWidth: 320, p: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {getTitle()}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
          {getMethodology()}
        </Typography>
        {data && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
              {getDataContext()}
            </Typography>
          </>
        )}
        {narrative && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ 
              fontStyle: 'italic', 
              fontSize: '0.75rem',
              opacity: 0.8
            }}>
              💡 {narrative}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  // Check if user needs onboarding (no organization assigned)
  useEffect(() => {
    logger.info('DashboardPage useEffect triggered:', {
      hasUser: !!user,
      organizationId: user?.organizationId,
      showOnboarding,
      hasLandingFormData: !!landingFormData,
      pathname: window.location.pathname
    });

    if (user && !user.organizationId) {
      // The showOnboarding state is already set in AuthContext
      setLoading(false);
    } else if (user && user.organizationId) {
      // Fetch dashboard data normally
      // NOTE: Navigation for landing form data is handled by OnboardingModal, not here
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async (silent: boolean = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await apiService.getDashboardSummary();
      setData(response);
      
      // Also fetch org info for the cancellation banner
      try {
        const orgResponse = await apiService.getSubscriptionInfo();
        setOrgInfo(orgResponse); // Data is at root level, not nested under 'organization'
      } catch (orgError) {
        logger.info('Could not fetch org info:', orgError);
      }
      
      // Fetch KPI data in parallel
      fetchKPIData();
      
    } catch (err: any) {
      logger.error('Dashboard data fetch error:', err);
      
      // Check if trial has expired or subscription is cancelled
      if (!handleTrialApiError(err)) {
        handleSubscriptionApiError(err);
      }
      
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Silent polling while recent items are in-progress (no reputation yet)
  useEffect(() => {
    if (!data) return;
    const needsPoll = (data.recentSimulations || []).some((s: any) => typeof s.reputationUIScore !== 'number' || !s.reputationLevel)
      || (data.recentIdeas || []).some((i: any) => !i.reputation || typeof i.reputation.uiScore !== 'number');
    if (!needsPoll) return;
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 7000);
    return () => clearInterval(interval);
  }, [data]);

  const fetchKPIData = async () => {
    try {
      setKpiLoading(true);
      setKpiError(null);
      
      // Fetch KPI data for last 30 days (default)
      const kpiResponse = await apiService.getKPIData();
      setKpiData(kpiResponse);
      
    } catch (err: any) {
      logger.error('KPI data fetch error:', err);
      setKpiError(err.message || 'Failed to load KPI data');
    } finally {
      setKpiLoading(false);
    }
  };

  if (loading && !isTrialExpiredModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If onboarding is required, show the modal and a minimal view
  if (showOnboarding) {
    return (
      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)}
        expressMode={!!landingFormData}
        landingFormData={landingFormData}
      />
    );
  }

  // If there's an error and no modal is active, show error message
  if (error && !data && !isTrialExpiredModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => fetchDashboardData()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Silent polling while recent items are in progress */}
      {(() => {
        const needsPoll = !!data && (
          (data.recentSimulations || []).some((s: any) => typeof s.reputationUIScore !== 'number' || !s.reputationLevel) ||
          (data.recentIdeas || []).some((i: any) => !i.reputation || typeof i.reputation.uiScore !== 'number')
        );
        // useEffect inline via IIFE is not allowed; place a hidden side effect using a component
        return null;
      })()}
      {orgInfo?.status === 'ACTIVE_UNTIL' && (
         <SubscriptionCancellationBanner 
            isAdmin={user?.role === 'ADMIN'} 
            expiryDate={orgInfo.nextBillingAt} 
         />
      )}

      <TrialStatusModal 
        isOpen={isTrialExpiredModalOpen} 
        onClose={closeTrialExpiredModal} 
        isAdmin={isTrialAdmin}
        status="expired"
      />
      
      {/* Show trial expired modal after login */}
      <TrialStatusModal 
        isOpen={showTrialExpiredModal} 
        onClose={() => setShowTrialExpiredModal(false)} 
        isAdmin={user?.role === 'ADMIN'}
        status="expired"
      />
      
      {/* Show trial expiring soon modal after login */}
      <TrialStatusModal 
        isOpen={showTrialExpiringModal} 
        onClose={() => setShowTrialExpiringModal(false)} 
        isAdmin={user?.role === 'ADMIN'}
        status="expiring"
        daysRemaining={daysUntilExpiry || undefined}
      />
      
      <SubscriptionExpiredModal 
        isOpen={isSubscriptionExpiredModalOpen} 
        onClose={closeSubscriptionExpiredModal} 
        isAdmin={isSubscriptionAdmin} 
      />

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        <Typography 
          variant="h4" 
          fontWeight="bold" 
          mb={3}
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          {user?.organizationName ? `${user.organizationName} Dashboard` : 'Dashboard'}
        </Typography>

        {/* CTA Section */}
        <CtaSection />

        {/* All KPI Cards - Reordered as requested */}
        {kpiError && !kpiLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {kpiError} - Impact analysis temporarily unavailable.
          </Alert>
        )}
        
        <Grid container spacing={2} mb={4} sx={{ px: { xs: 1, sm: 0 } }}>
          {/* Row 1: Impact Analysis Cards + Quota */}
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Potential Backlash Prevented"
              value={kpiLoading ? '--' : 
                (kpiData?.reputationBacklashPrevented ? 
                  formatLargeNumber(kpiData.reputationBacklashPrevented.avoidedNegativeImpressions) : 
                  '--'
                )
              }
              sublabel={kpiLoading ? 'Loading...' :
                (kpiData?.reputationBacklashPrevented ? 
                  `${kpiData.reputationBacklashPrevented.postsAvoided} risky ${kpiData.reputationBacklashPrevented.postsAvoided === 1 ? 'post' : 'posts'} detected` : 
                  'No data for last 30 days'
                )
              }
              icon={<TrendingDown fontSize="large" />}
              loading={kpiLoading}
              color={getKPIColor(kpiData?.reputationBacklashPrevented?.avoidedNegativeImpressions, 'prevented') as 'primary' | 'secondary' | 'error' | 'success' | 'info' | 'warning'}
              tooltip={createImpactTooltip('backlash-prevented', kpiData?.reputationBacklashPrevented, kpiData?.narratives?.backlashPrevented)}
              data-testid="kpi-backlash-prevented"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Potential Boost Achieved"
              value={kpiLoading ? '--' : 
                (kpiData?.reputationBoostAchieved ? 
                  formatLargeNumber(kpiData.reputationBoostAchieved.positiveImpressionsGained) : 
                  '--'
                )
              }
              sublabel={kpiLoading ? 'Loading...' :
                (kpiData?.reputationBoostAchieved ? 
                  `${kpiData.reputationBoostAchieved.postsCount} successful ${kpiData.reputationBoostAchieved.postsCount === 1 ? 'post' : 'posts'}` : 
                  'No data for last 30 days'
                )
              }
              icon={<TrendingUp fontSize="large" />}
              loading={kpiLoading}
              color={getKPIColor(kpiData?.reputationBoostAchieved?.positiveImpressionsGained, 'achieved') as 'primary' | 'secondary' | 'error' | 'success' | 'info' | 'warning'}
              tooltip={createImpactTooltip('boost-achieved', kpiData?.reputationBoostAchieved, kpiData?.narratives?.boostAchieved)}
              data-testid="kpi-boost-achieved"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Potential Net Impact"
              value={kpiLoading ? '--' : 
                (kpiData?.netImpact ? 
                  formatLargeNumber(kpiData.netImpact.impressions) : 
                  '--'
                )
              }
              sublabel={kpiLoading ? 'Loading...' :
                (kpiData?.netImpact ? 
                  `${formatReputationPoints(kpiData.netImpact.repPoints)} overall` : 
                  'No data for last 30 days'
                )
              }
              icon={<Balance fontSize="large" />}
              loading={kpiLoading}
              color={getKPIColor(kpiData?.netImpact?.impressions, 'net') as 'primary' | 'secondary' | 'error' | 'success' | 'info' | 'warning'}
              tooltip={createImpactTooltip('net-impact', kpiData?.netImpact, kpiData?.narratives?.netImpact)}
              data-testid="kpi-net-impact"
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Quota Used"
              value={
                orgInfo?.plan === 'INTERNAL' ? 'Unlimited' :
                orgInfo && typeof orgInfo.simUsed === 'number' && typeof orgInfo.monthlySimQuota === 'number' ? 
                  `${orgInfo.simUsed}/${orgInfo.monthlySimQuota}` :
                data ? `${data.kpis.quotaUsed}/${data.kpis.quotaLimit}` : '0/0'
              }
              icon={<DataUsage fontSize="large" />}
              loading={loading}
              color={
                orgInfo?.plan === 'INTERNAL' ? 'success' :
                orgInfo && orgInfo.monthlySimQuota ? 
                  ((orgInfo.simUsed || 0) / orgInfo.monthlySimQuota > 0.8 ? 'warning' : 'secondary') :
                  (data && data.kpis.quotaUsed / data.kpis.quotaLimit > 0.8 ? 'warning' : 'secondary')
              }
              sublabel={
                orgInfo?.plan === 'INTERNAL' ? 'Internal organization' :
                orgInfo?.trialEndsAt ? `Trial expires ${new Date(orgInfo.trialEndsAt).toLocaleDateString()}` :
                data?.kpis.quotaResetDate ? `Resets ${new Date(data.kpis.quotaResetDate).toLocaleDateString()}` : 
                'This billing period'
              }
              tooltip="Number of simulations used this billing period out of your monthly quota"
              data-testid="kpi-quota-used"
            />
          </Grid>

          {/* Row 2: Activity Cards */}
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Total Simulations"
              value={data?.kpis.totalSimulations || 0}
              icon={<AssessmentIcon fontSize="large" />}
              loading={loading}
              color="primary"
              tooltip="Total number of content simulations run by your organization"
              onClick={() => navigate('/simulations')}
              data-testid="kpi-total-simulations"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Total Ideas"
              value={data?.kpis.totalIdeas || 0}
              icon={<LightbulbIcon fontSize="large" />}
              loading={loading}
              color="secondary"
              tooltip="Total number of content ideas generated by your organization"
              onClick={() => navigate('/ideas')}
              data-testid="kpi-total-ideas"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Custom Personas"
              value={
                orgInfo?.plan === 'INTERNAL' 
                  ? (data?.kpis.activeICPs?.toString() || '0')
                  : data ? `${data.kpis.activeICPs}/${data.kpis.activeICPsLimit}` : '0/0'
              }
              icon={<GroupIcon fontSize="large" />}
              loading={loading}
              color="info"
              tooltip="Number of custom personas configured"
              onClick={() => navigate('/personas')}
              data-testid="kpi-active-icps"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              label="Social Accounts"
              value={
                orgInfo?.plan === 'INTERNAL' 
                  ? (data?.kpis.socialAccounts?.toString() || '0')
                  : data ? `${data.kpis.socialAccounts}/${data.kpis.socialAccountsLimit}` : '0/0'
              }
              icon={<TagIcon fontSize="large" />}
              loading={loading}
              color="success"
              tooltip="Number of social accounts"
              onClick={() => navigate('/accounts')}
              data-testid="kpi-social-accounts"
            />
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <RecentSimulations
              simulations={data?.recentSimulations || []}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RecentIdeas
              ideas={data?.recentIdeas || []}
              loading={loading}
            />
          </Grid>
        </Grid>

      </Box>
    </>
  );
};

export default DashboardPage;
